import { Client } from "app/bundles/requests/shared/models/client";
import { Service } from "app/bundles/requests/shared/models/service";
import { Status } from "app/bundles/requests/shared/models/status";
import { IDocument, OriginEnum } from "./document";
import { Geometry } from "./geometry";
import { EventRequestById } from "./event";

export interface IBaseRequest {
  source: RequestSourceTypeEnum;
  id: number;
  requestNumber: string;
  isRelaunched: boolean;
  isReactivated: boolean;
  description: string;
  responsible: {
    groupName: string;
    groupId: number;
    userName: string;
    userCode: string;
  };
  service: Service;
  status: Status;
  formattedAddress: string;
  borough: {
    code: number;
    name: string;
  };
  geometry: Geometry;
  creationDatetime: Date;
  estimatedCompletionDate: Date;
}

export interface IBaseRequestSummary extends IBaseRequest {
  isSelected: boolean;
  isFiltred: boolean;
  serviceRequestId: number;
}

export interface IBaseRequestDetail extends IBaseRequest {
  clients: Client[];

  // foremanMedias can be in both workorderRequest
  // AND serviceRequest
  // (bc of special case where workorderRequest has been cancelTakeInCharge while having photos in it.)
  foremanMedias: IForemanMedia[];

  requestSummary: IWorkorderOrServiceRequestSummary;
}

export interface IForemanMedia extends IDocument {
  origin: OriginEnum.foreman;
}

export interface ICitizenMedia extends IDocument {
  origin: OriginEnum.citizen;
}

export interface IWorkorderRequestSummary extends IBaseRequestSummary {
  source: RequestSourceTypeEnum.workorderRequest;
  type: workorderTypeEnum;
  hasExistingTask: boolean;
}

export interface IWorkorderRequestDetail extends IBaseRequestDetail {
  source: RequestSourceTypeEnum.workorderRequest;
  type: workorderTypeEnum;
  hasExistingTask: boolean;
  hasServiceRequestLinkedTo: boolean;
  serviceRequestDetail: IServiceRequestDetail;
  workorderRequestCreationDatetime: Date;
}

export interface IWorkorderRequestShallowDetail extends IBaseRequest {
  source: RequestSourceTypeEnum.workorderRequest;
  clients: Client[];
  type: workorderTypeEnum;
  hasExistingTask: boolean;
  hasServiceRequestLinkedTo: boolean;
  serviceRequestId?: number;
  workorderRequestCreationDatetime: Date;
}

export interface IServiceRequestDetail extends IBaseRequestDetail {
  source: RequestSourceTypeEnum.serviceRequest;
  citizenMedias: ICitizenMedia[];
  comments: IServiceRequestComment;
  events: EventRequestById[];
}

export interface IServiceRequestSummary extends IBaseRequestSummary {
  source: RequestSourceTypeEnum.serviceRequest;
  comments: IServiceRequestComment;
  events: EventRequestById[];
}

export interface IServiceRequestComment {
  code: string;
  "fr-CA": string;
  "en-CA": string;
}

// key MUST stay in camelCase so that
// de-serialization can work as expected
// (ex: RequestSourceTypeEnum["serviceRequest"])
export enum RequestSourceTypeEnum {
  serviceRequest = "serviceRequest",
  workorderRequest = "workorderRequest",
}

export type IWorkorderOrServiceRequestSummary =
  | IWorkorderRequestSummary
  | IServiceRequestSummary;

export type IWorkorderOrServiceRequestDetail =
  | IWorkorderRequestDetail
  | IServiceRequestDetail;

export enum workorderTypeEnum {
  external = "E",
  internal = "I",
}

export interface IRawServiceRequestSummary {
  serviceRequestId: number;
  requestNumber: string;
  status: {
    code: string;
    name: string;
    createdAt: Date;
  };
  comments?: IServiceRequestComment;
  requestedDatetime: Date;
  nbrDaysForResolution: number;
  media?: IDocument[];
  formattedAddress: string;
  borough: {
    code: number;
    name: string;
  };
  postalCode: string;
  activity: {
    code: string;
    name: string;
  };
  location: {
    lat: number;
    long: number;
  };
  nbrOfCommunications: number;
  attributes: any[];
  subject: string;
  isReactivated: boolean;
  isRelaunched: boolean;
  responsible: {
    groupName?: string;
    groupId?: number;
    userName?: string;
    userCode?: string;
  };
  updatedDatetime: Date;
}

export enum PhoneTypeEnum {
  HOME = "home",
  WORK = "work",
  CELL = "cell",
}

export interface IRawServiceRequestClient {
  name: string;
  phones: {
    type: PhoneTypeEnum;
    number: string;
    extension: string;
  }[];
  address: string;
  comment: string;
  email: string;
  creationDatetime: Date;
}

export interface IRawServiceRequestDetail {
  serviceRequestId: number;
  requestNumber: string;
  status: {
    code: string;
    name: string;
    createdAt: Date;
  };
  comments: IServiceRequestComment;
  requestedDatetime: Date;
  nbrDaysForResolution: number;
  media: IDocument[];
  formattedAddress: string;
  borough: {
    code: number;
    name: string;
  };
  postalCode: string;
  activity: {
    code: string;
    name: string;
  };
  location: {
    lat: number;
    long: number;
  };
  nbrOfCommunications: number;
  subject: string;
  isReactivated: boolean;
  isRelaunched: boolean;
  responsible: {
    groupName: string;
    groupId: number;
    userName: string;
    userCode: string;
  };
  updatedDatetime: Date;
  clients: IRawServiceRequestClient[];
  internalComments: {
    internalComment: string;
    creationDatetime: Date;
    userCreatedBy: {
      userCode: string;
      firstName: string;
      lastName: string;
    };
  }[];
}
